import { useState, useCallback } from 'react';
import { noop } from 'lodash';
import './FormatTypeInfo.scss';
import { Icon } from '@pwc/pwc-deals-ui';
import { isDisplayFormatEnabled } from 'common/helpers/featureFlags';
import { toggleLibraryItemFormatForClipboard } from 'features/contentLibrary/libraryItemService';
import SupportTicketLink from '../SupportTicketLink';

export type ResponseFormatTypeChange = {
  data: {
    worksheetName: string;
    name: string;
    uniqueId: string;
    formatType: string;
    siteId: string;
    driveId: string;
    driveItemId: string;
    sharePointItemUrl: string;
  };
  id: number;
  name: string;
  status: string;
  type: number;
};

export type FormatTypeInfoProps = {
  isHTML?: boolean;
  isEditMode?: boolean;
  show?: boolean;
  libraryItemId: string;
  projectId: string;
  handleClick?: (
    response: ResponseFormatTypeChange,
    libraryItemId: string,
    projectId: string
  ) => void;
  setLoading?: (libraryItemId: string, isLoading: boolean) => void;
  issueFound?: boolean;
  svgMinitableShow?: boolean;
};

type ExcelPPTItemInsideProp = {
  type?: number;
  autoSync?: {
    type?: string;
  };
};

// display only for 302, 301 type, only for edit mode
export const showFormatTypeInfo = ({
  type,
  autoSync,
}: ExcelPPTItemInsideProp): boolean => {
  if ((type && type === 302) || type === 301) return true;
  return ['ExcelCellRange', 'ExcelNamedRange'].includes(autoSync?.type || '');
};

const FormatTypeInfo = ({
  isHTML = true,
  isEditMode = false,
  show = true,
  libraryItemId,
  projectId,
  handleClick = noop,
  setLoading = noop,
  issueFound = false,
  svgMinitableShow = true,
}: FormatTypeInfoProps) => {
  const [switchToHTML, isSwitchToHTML] = useState<boolean>(isHTML);
  const [requestFailed, isRequestFailed] = useState<boolean>(false);

  const switchFormatType = useCallback(
    async (formatType: 'HTML' | 'SVG') => {
      setLoading(libraryItemId, true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response: ResponseFormatTypeChange =
          await toggleLibraryItemFormatForClipboard(projectId, libraryItemId, {
            formatType,
          });
        handleClick(response, libraryItemId, projectId);
        isSwitchToHTML(formatType === 'HTML');
        setLoading(libraryItemId, false);
        isRequestFailed(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setLoading(libraryItemId, false);
        isRequestFailed(true);
      }
    },
    [handleClick, libraryItemId, projectId, setLoading]
  );

  const displayedHTML = (
    <div
      className="content-item-link libraryItem-format-type-info format-type-HTML"
      data-testid="FTI-switcher-HTML"
      id={`${libraryItemId ?? ''}-switcher`}
      tabIndex={-1}
    >
      Display as <span className="format-type-name">Web Table</span> or{' '}
      <button
        id={`${libraryItemId ?? ''}-button`}
        className="ui-button button-link"
        data-testid="FTI-switcher-button"
        type="button"
        onClick={() => switchFormatType('SVG')}
      >
        Image
      </button>
    </div>
  );

  const displayedSVG = (
    <div
      className="content-item-link libraryItem-format-type-info format-type-SVG"
      data-testid="FTI-switcher-SVG"
      id={`${libraryItemId ?? ''}-switcher`}
      tabIndex={-1}
    >
      Display as{' '}
      <button
        type="button"
        id={`${libraryItemId ?? ''}-button`}
        className="ui-button button-link"
        data-testid="FTI-switcher-button"
        onClick={() => switchFormatType('HTML')}
      >
        Web Table
      </button>{' '}
      or <span className="format-type-name">Image</span>
    </div>
  );

  if (!isEditMode || !show || !isDisplayFormatEnabled) {
    return <></>;
  }

  const contentToDisplay = switchToHTML ? displayedHTML : displayedSVG;
  return (
    <>
      {(requestFailed || issueFound) && (
        <div
          className="container alert-box"
          data-testid="format-type-alert-box"
        >
          <Icon
            name="alert"
            width="16"
            height="24"
            data-testid="FTI-alert-request-failed"
            className="form-error-msg__icon"
          />{' '}
          Unable to display item as an {isHTML ? 'image' : 'web table'}, please
          try again or <SupportTicketLink />{' '}
        </div>
      )}
      {svgMinitableShow && contentToDisplay}
    </>
  );
};

export default FormatTypeInfo;
